/**
 * @fileoverview Browser router for the public website and production DNA app.
 *
 * This module turns route arrays from `src/config/routes.js` into React Router
 * route entries, keeps legacy `/dna` URLs redirected to `/life/dna`, and wraps
 * routed pages in the shared root layout.
 */

import { lazy, Suspense, useEffect } from 'react'
import { Navigate, Outlet, RouterProvider, createBrowserRouter, useLocation } from 'react-router-dom'
import {
  CONTENT_PAGE_ROUTES,
  LIFE_PUBLIC_LEGAL_ROUTES,
  DNA_APP_BASE_PATH,
  DNA_APP_ROUTES,
  DNA_LEGACY_APP_BASE_PATH,
  PAGE_TYPES,
  getCanonicalDnaPathFromLegacyPath,
} from '../config/routes'

const Home = lazy(() => import("./pages/home"))
const Pages = lazy(() => import('./Pages'))
const AboutPage = lazy(() => import('./pages/about'))
const PolicyPage = lazy(() => import('./pages/policies'))
const TermsPage = lazy(() => import('./pages/terms'))
const LifeLegalDocumentPage = lazy(() => import('./pages/LifeLegalDocumentPage'))
const DnaRouteView = lazy(() => import('../apps/dna/DnaRouteView'))

/**
 * Selects the public website page component for a content page type.
 *
 * @param {string} type
 * @returns {JSX.Element}
 */
const renderContentRoute = (type) => {
  if (type === PAGE_TYPES.ABOUT) {
    return <AboutPage />
  }

  if (type === PAGE_TYPES.POLICY) {
    return <PolicyPage />
  }

  if (type === PAGE_TYPES.TERMS) {
    return <TermsPage />
  }

  return <Pages type={type} />
}

/**
 * Disables browser scroll restoration so routed pages control their own scroll position.
 *
 * @returns {null}
 */
function ScrollBehavior() {
  useEffect(() => {
    if (typeof window === 'undefined' || !('scrollRestoration' in window.history)) {
      return undefined
    }

    const previousValue = window.history.scrollRestoration
    window.history.scrollRestoration = 'manual'

    return () => {
      window.history.scrollRestoration = previousValue
    }
  }, [])

  return null
}

/**
 * Shared layout for every public website and DNA app route.
 *
 * @returns {JSX.Element}
 */
function RootLayout() {
  return (
    <>
      <ScrollBehavior />
      <Suspense fallback={<div className="App" />}>
        <Outlet />
      </Suspense>
    </>
  )
}

/**
 * Redirects legacy `/dna` URLs to the canonical `/life/dna` route tree.
 *
 * @returns {JSX.Element}
 */
function LegacyDnaRedirect() {
  const location = useLocation()

  return (
    <Navigate
      to={getCanonicalDnaPathFromLegacyPath(location.pathname, location.search, location.hash)}
      replace
    />
  )
}

/**
 * React Router route tree for the full website.
 */
const appRouter = createBrowserRouter([
  {
    path: '/',
    element: <RootLayout />,
    children: [
      { index: true, element: <Home /> },
      ...LIFE_PUBLIC_LEGAL_ROUTES.map(({ path, slug, label }) => ({
        path,
        element: <LifeLegalDocumentPage key={slug} slug={slug} title={label} />,
      })),
      ...CONTENT_PAGE_ROUTES.map(({ path, type }) => ({
        path,
        element: renderContentRoute(type),
      })),
      { path: DNA_LEGACY_APP_BASE_PATH, element: <LegacyDnaRedirect /> },
      { path: `${DNA_LEGACY_APP_BASE_PATH}/*`, element: <LegacyDnaRedirect /> },
      ...DNA_APP_ROUTES.map(({ path, type }) => ({
        path,
        element: <DnaRouteView routeType={type} />,
      })),
    ],
  },
])

/**
 * Provides the app router to the React tree.
 *
 * @returns {JSX.Element}
 */
function AppRouter() {
  return <RouterProvider router={appRouter} fallbackElement={<div className="App" />} />
}

export default AppRouter
